import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, ClipboardCheck, Globe, Rocket, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const initial = {
  draft_id:"",seller_legal_name:"Lazaro Ariel Fortun",privacy_policy_url:"",
  disclosure_version:"",disclosure_text:"",review_reference:"",
  buyer_names:"",buyer_sharing_text:"",sms_consent_text:"",call_consent_text:"",
  compliance_review_attested:false,
};
const field = "mt-1 block w-full min-w-0 rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900";
const button = "inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40";

export default function CampaignLaunchpad(){
  const [step,setStep]=useState(0);
  const [drafts,setDrafts]=useState([]);
  const [cap,setCap]=useState({capture_enabled:false});
  const [form,setForm]=useState(initial);
  const [busy,setBusy]=useState(false);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState("");
  const [done,setDone]=useState(null);
  const set=(key,value)=>setForm(s=>({...s,[key]:value}));
  useEffect(()=>{
    let active=true;
    Promise.all([
      api.get("/platform/campaign-director/drafts"),
      api.get("/platform/lead-marketplace/capabilities"),
    ]).then(([a,b])=>{
      if(!active)return;
      const ds=a.data||[];
      setDrafts(ds);
      setCap(b.data||{capture_enabled:false});
      setForm(f=>({...f,draft_id:f.draft_id||ds[0]?.id||""}));
    }).catch(err=>{if(active)setError(formatApiErrorDetail(err?.response?.data?.detail)||"Cannot load campaign launch configuration.");})
      .finally(()=>{if(active)setLoading(false);});
    return()=>{active=false};
  },[]);

  const selected=drafts.find(d=>d.id===form.draft_id);
  const named=form.buyer_names.split(",").map(s=>s.trim()).filter(Boolean);
  const validDisclosure=form.privacy_policy_url.startsWith("https://")
    && form.disclosure_version.trim().length>=6
    && form.disclosure_text.trim().length>=80
    && form.review_reference.trim().length>=6
    && form.seller_legal_name.trim().length>=3
    && (named.length===0 ? !form.buyer_sharing_text.trim()
      : named.length<=5 && named.every(name=>form.buyer_sharing_text.toLowerCase().includes(name.toLowerCase())))
    && form.compliance_review_attested;

  const submit=async()=>{
    if(busy||!validDisclosure||!selected)return;
    if(!window.confirm(cap.capture_enabled
      ? "Create this legally reviewed landing campaign and enable public intake? This starts collecting consumer information but does NOT buy or publish ads, send SMS, place calls, or deliver leads."
      : "Save this reviewed campaign as a draft? Public intake is disabled by the deployment and will NOT be activated."))return;
    setBusy(true);setError("");
    let campaign=null;
    try{
      const payload={...form,buyer_names:named};
      const created=await api.post("/platform/lead-marketplace/campaigns",payload);
      campaign=created.data;
      if(cap.capture_enabled){
        try{
          const activated=await api.post(`/platform/lead-marketplace/campaigns/${campaign.id}/activate`);
          campaign=activated.data;
        }catch(error){
          setError("Campaign saved as draft, but intake activation failed. Open Lead Marketplace to inspect the deployment settings. Do not create a duplicate.");
        }
      }
      setDone(campaign);
    }catch(err){
      setError(formatApiErrorDetail(err?.response?.data?.detail)||"Could not create the reviewed campaign.");
    }finally{setBusy(false);}
  };

  const textInput=(key,label,required=true)=>(
    <label className="block text-sm font-medium text-slate-800">
      {label}<input className={field} required={required} value={form[key]}
        onChange={e=>set(key,e.target.value)} data-testid={`launch-field-${key}`}/>
    </label>
  );
  const longInput=(key,label,rows=3)=>(
    <label className="block text-sm font-medium text-slate-800">
      {label}<textarea className={field} rows={rows} value={form[key]}
        onChange={e=>set(key,e.target.value)} data-testid={`launch-field-${key}`}/>
    </label>
  );

  if(loading)return <div className="p-6 text-sm">Loading campaign configuration…</div>;
  return (
    <main className="mx-auto max-w-5xl space-y-6 pb-10" data-testid="campaign-launchpad-page">
      <header className="rounded-[22px] bg-[#0B1B2C] p-5 text-white sm:p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#D1A656]">Fortunate Operations / Campaign Studio</p>
        <h1 className="mt-3 font-heading text-3xl font-bold">Three-step campaign launch</h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
          Select a creative strategy, confirm reviewed consumer permissions, and launch a first-party Final Expense landing page.
          Ad purchasing, AI outreach and buyer delivery are separate gated operations.
        </p>
      </header>
      <nav aria-label="Campaign launch steps" className="grid grid-cols-3 gap-2">
        {["01 Strategy","02 Compliance","03 Review & launch"].map((label,i)=>
          <div key={label} aria-current={step===i?"step":undefined}
            className={`min-w-0 rounded-lg border p-3 text-center text-xs font-semibold sm:text-sm ${step===i?"border-[#D1A656] bg-[#FBF7EF] text-[#0B1B2C]":"border-slate-200 bg-white text-slate-500"}`}>
            {label}
          </div>)}
      </nav>
      {error&&<p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>}

      {done ? <section className="space-y-4 rounded-2xl border bg-white p-6" data-testid="launch-completed">
          <CheckCircle2 className="h-9 w-9 text-emerald-700"/>
          <h2 className="font-heading text-2xl font-semibold">Campaign {done.status==="active"?"active":"saved as a draft"}</h2>
          <p className="text-sm text-slate-600">{done.campaign_name}</p>
          {done.status==="active"&&
            <a className="block break-all text-sm font-semibold underline" href={`/final-expense/${done.public_token}`}
              target="_blank" rel="noreferrer">
              {window.location.origin}/final-expense/{done.public_token}
            </a>}
          {done.status!=="active"&&<p className="text-sm text-amber-800">Public intake remains disabled. No consumers can submit this campaign yet.</p>}
          <div className="flex flex-wrap gap-2">
            <Link className={button} to="/lead-marketplace">Manage campaign</Link>
            <Link className={button} to="/content-studio">Review campaign content</Link>
          </div>
        </section>
        : <section className="space-y-5 rounded-2xl border bg-white p-4 sm:p-7">
          {step===0&&<div className="space-y-4" data-testid="launch-step-1">
            <h2 className="font-heading text-xl font-bold text-[#0B1B2C]">1. Choose your campaign strategy</h2>
            <p className="text-sm text-slate-600">Use an existing AI draft. Generating a new draft costs OpenAI credits only when explicitly confirmed in Campaign Director.</p>
            {!drafts.length&&<p className="rounded-lg border p-4 text-sm">
              No campaign strategy exists yet. <Link className="font-semibold underline" to="/campaign-director">Create a strategy in Campaign Director</Link> first.
            </p>}
            {drafts.length>0&&<label className="block text-sm font-medium">
              Draft strategy
              <select className={field} value={form.draft_id} onChange={e=>set("draft_id",e.target.value)} data-testid="launch-draft">
                {drafts.map(d=><option key={d.id} value={d.id}>{d.proposal?.name||d.id}</option>)}
              </select>
            </label>}
            {selected&&<div className="space-y-2 rounded-lg border bg-slate-50 p-4 text-sm">
              <strong>{selected.proposal?.headline}</strong>
              <p>{selected.proposal?.body}</p>
              <p className="text-xs text-slate-500">Target: {selected.lead_target} inquiries · FL Final Expense · Unpublished</p>
            </div>}
            <div className="flex flex-wrap gap-2">
              <Link className={button} to="/content-studio">Create reviewed content variants <ArrowRight className="h-4 w-4"/></Link>
              <button className={button+" border-[#D1A656] bg-[#D1A656] text-[#0B1B2C]"} type="button"
                disabled={!selected} onClick={()=>setStep(1)} data-testid="launch-next-1">
                Continue <ArrowRight className="h-4 w-4"/>
              </button>
            </div>
          </div>}
          {step===1&&<div className="space-y-4" data-testid="launch-step-2">
            <h2 className="font-heading text-xl font-bold text-[#0B1B2C]">2. Review your legal identity and permissions</h2>
            <p className="text-sm text-slate-600">These must be real approved disclosures. Do not paste suggested AI language and claim an approval that did not occur.</p>
            {textInput("seller_legal_name","Legal business operator")}
            {textInput("privacy_policy_url","Published HTTPS privacy policy URL")}
            <div className="grid gap-3 sm:grid-cols-2">
              {textInput("disclosure_version","Reviewed disclosure version")}
              {textInput("review_reference","Legal/compliance review reference")}
            </div>
            {longInput("disclosure_text","Reviewed consumer request and privacy disclosure",4)}
            {textInput("buyer_names","Specifically named licensed buyers (optional, comma-separated)",false)}
            {named.length>0&&longInput("buyer_sharing_text","Optional buyer-sharing permission naming every intended buyer")}
            <details className="rounded-lg border p-3 text-sm">
              <summary className="cursor-pointer font-medium">Optional SMS / AI-call permission language</summary>
              <p className="my-2 text-xs text-slate-500">Blank means the channel cannot be consented to on the page. SMS and calls are never started by this wizard.</p>
              {longInput("sms_consent_text","Approved optional SMS opt-in text")}
              {longInput("call_consent_text","Approved optional call and AI voice opt-in text")}
            </details>
            <label className="flex items-start gap-3 rounded-lg bg-[#FBF7EF] p-4 text-sm">
              <input type="checkbox" className="mt-1" checked={form.compliance_review_attested}
                onChange={e=>set("compliance_review_attested",e.target.checked)}/>
              <span>I attest that the legal identity, published privacy policy, disclosures, and named buyer permissions were independently reviewed and approved for this campaign.</span>
            </label>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={button} onClick={()=>setStep(0)}><ArrowLeft className="h-4 w-4"/>Back</button>
              <button type="button" className={button+" border-[#D1A656] bg-[#D1A656] text-[#0B1B2C]"}
                disabled={!validDisclosure} data-testid="launch-next-2" onClick={()=>setStep(2)}>
                Review campaign <ArrowRight className="h-4 w-4"/>
              </button>
            </div>
          </div>}
          {step===2&&<div className="space-y-4" data-testid="launch-step-3">
            <h2 className="font-heading text-xl font-bold text-[#0B1B2C]">3. Confirm and create your campaign</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border p-4 text-sm">
                <Globe className="mb-2 h-5 w-5 text-[#AC8441]"/>
                <p className="font-semibold">First-party landing page</p>
                <p className="mt-1 text-slate-600">{selected?.proposal?.name}</p>
              </div>
              <div className="rounded-lg border p-4 text-sm">
                <ClipboardCheck className="mb-2 h-5 w-5 text-[#AC8441]"/>
                <p className="font-semibold">Reviewed consent</p>
                <p className="mt-1 text-slate-600">{form.disclosure_version} · {named.length} named buyers</p>
              </div>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="launch-capability">
              {cap.capture_enabled
                ? "Public intake is enabled on this deployment. Creating the campaign will also activate its reviewed landing page."
                : "Public intake is OFF at the server. You can create the campaign draft, but it cannot collect leads until you deliberately enable intake in deployment settings."}
              <p className="mt-2">No Google/Meta ads, SMS campaigns, outbound AI calls, or lead deliveries will start.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={button} onClick={()=>setStep(1)} disabled={busy}><ArrowLeft className="h-4 w-4"/>Edit disclosures</button>
              <button type="button" className={button+" border-[#D1A656] bg-[#D1A656] text-[#0B1B2C]"}
                disabled={busy||!validDisclosure||!selected} onClick={submit} data-testid="launch-submit">
                <Rocket className="h-4 w-4"/>{busy?"Creating…":cap.capture_enabled?"Launch reviewed intake":"Save campaign draft"}
              </button>
            </div>
          </div>}
        </section>}
      {!done&&<p className="text-xs text-slate-500">
        Exclusive reservation is controlled separately in the Buyer Marketplace. A lead is not automatically sold, shared or assigned to an agency.
      </p>}
      <footer className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
        <ShieldCheck className="h-4 w-4"/> Every page is owner-scoped. No background charges, ads or texts are triggered.
      </footer>
    </main>
  );
}
